import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { removeAtIndex } from '../utils/array';

function getDisplayName(component) {
  return component.displayName || component.name || 'Component';
}

const defaultGetEntity = props => props.playlist;

function defaultGetTrackIds(props) {
  return props.playlist.tracks.map(({ id }) => id);
}

export const editableEntityPropTypes = {
  isEditable: PropTypes.bool,
  playlist: PropTypes.object,
  collectionIds: PropTypes.object,
  onRename: PropTypes.func,
  onDelete: PropTypes.func,
  onDeleteTrack: PropTypes.func,
  onReorder: PropTypes.func,
  onBatchDeleteTracks: PropTypes.func,
  onBatchAddToPlaylistClick: PropTypes.func,
  onBatchAddToCollection: PropTypes.func,
  onBatchRemoveFromCollection: PropTypes.func,
  showDeleteTracksModal: PropTypes.func,
};

export default function editableEntity(
  actions,
  getIsEditable,
  getEntity = defaultGetEntity,
  getTrackIds = defaultGetTrackIds,
  options = {}
) {
  const { withRef = false } = options;

  return WrappedComponent => {
    class Editable extends Component {
      static propTypes = editableEntityPropTypes;

      onRename = () => {
        this.props.onRename(getEntity(this.props));
      };

      onDelete = () => {
        this.props.onDelete(getEntity(this.props));
      };

      onDeleteTrack = (track, index) => {
        this.props.showDeleteTracksModal(1, () => {
          const playlist = getEntity(this.props);
          const trackIds = getTrackIds(this.props);
          const updatedTrackIds = removeAtIndex(trackIds, index);
          const action = {
            type: 'remove',
            trackId: track.id,
            index,
            trackIds: updatedTrackIds,
          };
          this.props.onDeleteTrack(playlist, action);
        });
      };

      onReorder = (updatedTrackIds, change) => {
        const playlist = getEntity(this.props);
        const action = {
          type: 'move',
          trackIds: updatedTrackIds,
          ...change, // from, to, trackId
        };
        this.props.onReorder(playlist, action);
      };

      onBatchDeleteTracks = (selectedIndices, onComplete) => {
        if (selectedIndices.size === 0) {
          return;
        }
        this.props.showDeleteTracksModal(selectedIndices.size, async () => {
          const playlist = getEntity(this.props);
          const trackIds = getTrackIds(this.props);

          const selectedTrackIds = this.getSelectedTrackIds(selectedIndices);
          const remainingTrackIds = trackIds.filter((_, i) => !selectedIndices.has(i));

          const op = {
            type: 'batchRemove',
            sortedIndices: Array.from(selectedIndices).sort((a, b) => b - a),
            selectedTrackIds,
            remainingTrackIds,
          };

          await this.props.onBatchDeleteTracks(playlist, op);
          if (onComplete) {
            onComplete();
          }
        });
      };

      onBatchAddToPlaylistClick = selectedIndices => {
        if (selectedIndices.size === 0) {
          return;
        }
        const selectedTrackIds = this.getSelectedTrackIds(selectedIndices);
        this.props.onBatchAddToPlaylistClick(selectedTrackIds);
      };

      onBatchToggleCollection = selectedIndices => {
        if (selectedIndices.size === 0) {
          return;
        }
        if (this.areAllSelectedTracksInCollection(selectedIndices)) {
          this.onBatchRemoveFromCollection(selectedIndices);
        } else {
          this.onBatchAddToCollection(selectedIndices);
        }
      };

      onBatchAddToCollection = selectedIndices => {
        if (selectedIndices.size === 0) {
          return;
        }
        const selectedTrackIds = this.getSelectedTrackIds(selectedIndices);
        this.props.onBatchAddToCollection(selectedTrackIds);
      };

      onBatchRemoveFromCollection = selectedIndices => {
        if (selectedIndices.size === 0) {
          return;
        }
        const selectedTrackIds = this.getSelectedTrackIds(selectedIndices);
        this.props.onBatchRemoveFromCollection(selectedTrackIds);
      };

      getSelectedTrackIds = selectedIndices => {
        const playlist = getEntity(this.props);
        return Array.from(selectedIndices).map(index => playlist.tracks[index].id);
      };

      render() {
        // only is editable if has all actions and has `isEditable`
        // from mapStateToProps
        const isEditable =
          !!actions.onRename &&
          !!actions.onDelete &&
          !!actions.onDeleteTrack &&
          !!actions.onReorder &&
          this.props.isEditable; // <- comes from mapStateToProps

        return (
          <WrappedComponent
            {...this.props}
            ref={withRef ? 'wrappedInstance' : null}
            isEditable={isEditable}
            onRename={this.onRename}
            onDelete={this.onDelete}
            onDeleteTrack={this.onDeleteTrack}
            onReorder={this.onReorder}
            onBatchDeleteTracks={this.onBatchDeleteTracks}
            onBatchAddToPlaylistClick={this.onBatchAddToPlaylistClick}
            onBatchToggleCollection={this.onBatchToggleCollection}
            showDeleteTracksModal={this.props.showDeleteTracksModal}
            areAllSelectedTracksInCollection={this.areAllSelectedTracksInCollection}
          />
        );
      }

      areAllSelectedTracksInCollection = selectedIndices => {
        const { playlist, collectionIds } = this.props;

        if (selectedIndices.size === 0 || !collectionIds) {
          return false;
        }

        const { entities, loaded } = collectionIds;
        if (!loaded || !entities || !entities.trackIds) {
          return false;
        }

        const trackIdsInCollection = entities.trackIds.map(id => id.toString());
        return Array.from(selectedIndices).every(index => {
          const trackId = playlist.tracks[index] ? playlist.tracks[index].id.toString() : null;
          return trackId && trackIdsInCollection.includes(trackId);
        });
      };
    }

    Editable.displayName = `Editable(${getDisplayName(WrappedComponent)}`;

    Editable.WrappedComponent = WrappedComponent;

    return connect(
      (state, ownProps) => ({
        isEditable: getIsEditable(state, getEntity(ownProps)),
      }),
      {
        onRename: actions.onRename,
        onDelete: actions.onDelete,
        onDeleteTrack: actions.onDeleteTrack,
        onReorder: actions.onReorder,
        onBatchDeleteTracks: actions.onBatchDeleteTracks,
        onBatchAddToPlaylistClick: actions.onBatchAddToPlaylistClick,
        onBatchAddToCollection: actions.onBatchAddToCollection,
        onBatchRemoveFromCollection: actions.onBatchRemoveFromCollection,
        showDeleteTracksModal: actions.showDeleteTracksModal,
      }
    )(Editable);
  };
}
